/**
 * apiFetch — Cluster A central API client (Feb 2026).
 *
 * Wraps `fetch` with:
 *   • automatic Authorization: Bearer <access_token>
 *   • on 401 → attempt one `supabase.auth.refreshSession()`, retry
 *   • on a second 401 → queue the call, emit a `bayenah:reauth-needed`
 *     event for `ReauthBanner`, await re-auth, then replay the call
 *
 * The banner is responsible for emitting `bayenah:reauth-succeeded`
 * when the user re-authenticates; this module listens for it and
 * resolves every queued call with a fresh token.
 *
 * Callers receive the standard `Response` they would have received
 * from `fetch`, so existing `r.ok` / `r.json()` patterns work
 * unchanged. The wrapper is `safeFetch`-aware: it routes through
 * `safeFetch` when available so the third-party `fetch`-patching
 * issue documented in `lib/safeFetch.js` is still avoided.
 */
import { supabase } from '@/lib/supabase';
import { safeFetch } from '@/lib/safeFetch';

const REAUTH_QUEUE = [];
let REAUTH_PENDING = false;

const dispatchReauthNeeded = (reason) => {
  if (REAUTH_PENDING) return;
  REAUTH_PENDING = true;
  try {
    window.dispatchEvent(new CustomEvent('bayenah:reauth-needed', { detail: { reason } }));
  } catch {
    /* SSR / window-missing — best-effort */
  }
};

const drainQueueAfterReauth = (newToken) => {
  REAUTH_PENDING = false;
  const queue = REAUTH_QUEUE.splice(0, REAUTH_QUEUE.length);
  queue.forEach(({ resolve, reject, input, init }) => {
    apiFetch(input, init, { _replayToken: newToken })
      .then(resolve)
      .catch(reject);
  });
};

if (typeof window !== 'undefined') {
  window.addEventListener('bayenah:reauth-succeeded', (e) => {
    const newToken = e?.detail?.accessToken || null;
    drainQueueAfterReauth(newToken);
  });
  window.addEventListener('bayenah:reauth-cancelled', () => {
    REAUTH_PENDING = false;
    const queue = REAUTH_QUEUE.splice(0, REAUTH_QUEUE.length);
    queue.forEach(({ reject }) => reject(new Error('reauth_cancelled')));
  });

  // QA debug hook (Cluster B Suite 1 Step 6, iter75 RCA): Supabase
  // caches the access_token in memory, so a localStorage clear is
  // NOT sufficient to provoke a real 401 from the backend. To make
  // the reauth-banner flow testable without actually revoking the
  // token server-side, expose TWO QA hooks:
  //
  //   1. window.__BAYENAH_QA_FORCE_REAUTH = true   (flag)
  //      Forces the next apiFetch into the 401-queue-reauth flow.
  //      Cleared after a single use.
  //
  //   2. window.__BAYENAH_QA_TRIGGER_REAUTH()      (function)
  //      Imperatively dispatches `bayenah:reauth-needed` so the
  //      banner mounts even when the failed call did not flow
  //      through apiFetch (e.g., axios, EventSource, raw fetch).
  //      A subsequent successful reauth resolves the empty queue.
  window.__BAYENAH_QA_FORCE_REAUTH = window.__BAYENAH_QA_FORCE_REAUTH || false;
  window.__BAYENAH_QA_TRIGGER_REAUTH = () => {
    dispatchReauthNeeded('qa_trigger');
  };
}

const getCurrentToken = async () => {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  } catch {
    return null;
  }
};

const tryRefresh = async () => {
  try {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) return null;
    return data?.session?.access_token || null;
  } catch {
    return null;
  }
};

const buildInit = (init, token) => {
  const next = { ...(init || {}) };
  const hdrs = new Headers(next.headers || {});
  if (token) hdrs.set('Authorization', `Bearer ${token}`);
  if (next.body && !hdrs.has('Content-Type') && typeof next.body === 'string') {
    hdrs.set('Content-Type', 'application/json');
  }
  next.headers = hdrs;
  return next;
};

/**
 * apiFetch(input, init, _opts)
 * @param input string | URL — same as fetch
 * @param init  RequestInit  — same as fetch (Authorization is added)
 * @param _opts internal — { _replayToken } skips refresh on first call
 */
export const apiFetch = async (input, init = {}, _opts = {}) => {
  const replayToken = _opts._replayToken;
  let token = replayToken || (await getCurrentToken());

  // QA debug hook (iter75): when window.__BAYENAH_QA_FORCE_REAUTH is
  // truthy, treat the next non-replay request as if the backend
  // returned 401 — go straight to the queue + reauth flow. Cleared
  // after a single use so a successful reauth replay can land.
  let res;
  if (!replayToken && typeof window !== 'undefined' && window.__BAYENAH_QA_FORCE_REAUTH) {
    window.__BAYENAH_QA_FORCE_REAUTH = false;
    res = new Response('{"detail":"qa_force_reauth"}', { status: 401 });
  } else {
    res = await safeFetch(input, buildInit(init, token));
  }
  if (res.status !== 401) return res;

  // First 401 → attempt refresh + single retry.
  if (!replayToken) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      res = await safeFetch(input, buildInit(init, refreshed));
      if (res.status !== 401) return res;
    }
  }

  // Second 401 → queue and request user re-auth.
  // Cluster A safety: the queue is *time-bounded*. If the banner is
  // unmounted or the user dismisses the prompt the call still fails
  // cleanly instead of hanging the caller's `await` indefinitely.
  // Callers that wrap apiFetch in fire-and-forget code see a clean
  // rejection after the timeout and continue rendering.
  const QUEUE_TIMEOUT_MS = 30_000;
  return new Promise((resolve, reject) => {
    const entry = { resolve, reject, input, init };
    REAUTH_QUEUE.push(entry);
    const tid = setTimeout(() => {
      const idx = REAUTH_QUEUE.indexOf(entry);
      if (idx !== -1) {
        REAUTH_QUEUE.splice(idx, 1);
        reject(new Error('apiFetch: reauth queue timed out'));
      }
    }, QUEUE_TIMEOUT_MS);
    // Wrap resolve/reject to clear the timer if we drain normally.
    const origResolve = entry.resolve;
    const origReject = entry.reject;
    entry.resolve = (v) => { clearTimeout(tid); origResolve(v); };
    entry.reject  = (e) => { clearTimeout(tid); origReject(e); };
    dispatchReauthNeeded(res.status === 401 ? 'token_expired' : 'unknown');
  });
};

/**
 * Convenience wrapper for JSON responses. Throws on non-2xx with a
 * structured Error carrying `.status` and `.body`.
 */
export const apiJson = async (input, init) => {
  const r = await apiFetch(input, init);
  if (!r.ok) {
    let body = '';
    try { body = await r.clone().text(); } catch { /* ignore */ }
    const err = new Error(`apiFetch ${r.status}: ${body || r.statusText}`);
    err.status = r.status;
    err.body = body;
    throw err;
  }
  return r.json();
};

export default apiFetch;
